import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Store
import { useAuthStore } from './store';

// Pages
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Routers from './pages/Routers/Routers';
import Users from './pages/Users/Users';
import Plans from './pages/Plans/Plans';
import Vouchers from './pages/Vouchers/Vouchers';
import Sessions from './pages/Sessions/Sessions';
// import Analytics from './pages/Analytics/Analytics';
import Tenants from './pages/Tenants/Tenants';
import VpnUsers from './pages/VpnUsers/VpnUsers'; // L2TP/IPsec VPN credentials for CGNAT router reachability
import TenantProfile from './pages/Tenants/TenantProfile';
import Branches from './pages/Branches/Branches';
import Landing from './pages/Portal/Landing';
import Welcome from './pages/Welcome/Welcome';
import Admins from './pages/Admins/Admins';
import ThemeDemo from './pages/ThemeDemo/ThemeDemo';
import Templates from './pages/Templates/Templates';
import Media from './pages/Media/Media';
import LandingPages from './pages/LandingPages/LandingPages';
import LandingBuilder from './pages/LandingBuilder/LandingBuilder';
import LandingTemplates from './pages/LandingTemplates/LandingTemplates';
import UpgradeApprovals from './pages/UpgradeApprovals/UpgradeApprovals';
import PaymentConfig from './pages/PaymentConfig/PaymentConfig';
import Devices from './pages/Devices/Devices'; // New
import Campaigns from './pages/Campaigns/Campaigns'; // New
import Billing from './pages/Billing/Billing'; // New
import Rooms from './pages/Rooms/Rooms'; // Hotel Room Management
import BranchPages from './pages/BranchPages/BranchPages'; // Custom per-branch pages (Staff/Banquet/etc)
import Integrations from './pages/Integrations/Integrations'; // PMS Integrations
import Settings from './pages/Settings/Settings'; // Platform Settings (global OTP delivery config)
import DOTLogs from './pages/DOTLogs/DOTLogs'; // DoT Compliance Logs
import UserTypes from './pages/UserTypes/UserTypes'; // WiFi Guest User Types
import PlanTemplates from './pages/PlanTemplates/PlanTemplates'; // Reusable Plan Templates
import VlanProfiles from './pages/VlanProfiles/VlanProfiles'; // VLAN-Based Plan Policies
import DeviceStatus from './pages/DeviceStatus/DeviceStatus'; // Network device online/offline status report
import AdminActivity from './pages/AdminActivity/AdminActivity'; // Admin login/activity audit trail
import GuestActivity from './pages/GuestActivity/GuestActivity'; // What each guest does on the hotspot
import SessionReport from './pages/SessionReport/SessionReport'; // Per-user session usage dashboard
import ConnectedUsers from './pages/ConnectedUsers/ConnectedUsers'; // Live Plan Upgrade/Downgrade
import MembershipTiers from './pages/MembershipTiers/MembershipTiers'; // Guest Loyalty Tiers
import Addons from './pages/Addons/Addons'; // Internet Add-On Store
import PolicyRules from './pages/PolicyRules/PolicyRules'; // Combined-condition Policy Engine
import Reports from './pages/Reports/Reports'; // Usage/Revenue/Plan-Sales/User-Status Reports
import SchedulerJobs from './pages/SchedulerJobs/SchedulerJobs'; // Plan Scheduler control panel
import Webhooks from './pages/Webhooks/Webhooks'; // Outbound Webhooks
import Traffic from './pages/Traffic/Traffic'; // Interface Traffic Analytics
import AuthSets from './pages/AuthSets/AuthSets'; // Authorization Sets
import AccessControl from './pages/AccessControl/AccessControl'; // Role Access Control
import Syslog from './pages/Syslog/Syslog'; // Enterprise Syslog Dashboard
import SetupGuide from './pages/SetupGuide/SetupGuide'; // Setup & Configuration Guide
import DeviceAuth from './pages/DeviceAuth/DeviceAuth'; // Device Authorization Live View
import AuthLogs from './pages/AuthLogs/AuthLogs'; // Authorization Session Audit Logs
import RadiusReport from './pages/RadiusReport/RadiusReport'; // RADIUS-style auth log report
import AuthLogDetail from './pages/AuthLogs/AuthLogDetail'; // Authorization Session Detail
import LogsHub from './pages/Logs/LogsHub'; // Centralized Hotspot Logs Hub
import SessionInvestigation from './pages/Logs/SessionInvestigation'; // Session investigation timeline
import PmsLogs from './pages/PmsLogs/PmsLogs'; // PMS Activity Logs (MongoDB)

// Layouts
import MainLayout from './layouts/MainLayout';

// Create React Query client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Public Route
const PublicRoute = ({ children }) => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Role-based Route Protection — superadmin always passes regardless of roles list
const RoleRoute = ({ children, roles }) => {
    const admin = useAuthStore((state) => state.admin);
    if (!admin) return <Navigate to="/login" replace />;
    if (admin.role === 'superadmin' || roles.includes(admin.role)) return children;
    return <Navigate to="/dashboard" replace />;
};

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <Routes>
                    {/* Public Routes */}
                    <Route
                        path="/login"
                        element={
                            <PublicRoute>
                                <Login />
                            </PublicRoute>
                        }
                    />

                    {/* Captive Portal Routes - Public */}
                    <Route path="/landing" element={<Landing />} />
                    <Route path="/welcome" element={<Welcome />} />

                    {/* Protected Routes */}
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <MainLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate to="/dashboard" replace />} />
                        <Route path="dashboard" element={<Dashboard />} />

                        {/* Superadmin only */}
                        <Route path="tenants" element={
                            <RoleRoute roles={['superadmin']}>
                                <Tenants />
                            </RoleRoute>
                        } />
                        <Route path="tenants/:id/profile" element={
                            <RoleRoute roles={['superadmin']}>
                                <TenantProfile />
                            </RoleRoute>
                        } />
                        <Route path="vpn-users" element={
                            <RoleRoute roles={['superadmin']}>
                                <VpnUsers />
                            </RoleRoute>
                        } />
                        <Route path="billing" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin']}>
                                <Billing />
                            </RoleRoute>
                        } />
                        <Route path="settings" element={
                            <RoleRoute roles={['superadmin']}>
                                <Settings />
                            </RoleRoute>
                        } />
                        <Route path="payment-config" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin']}>
                                <PaymentConfig />
                            </RoleRoute>
                        } />
                        <Route path="user-types" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <UserTypes />
                            </RoleRoute>
                        } />
                        <Route path="plan-templates" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin']}>
                                <PlanTemplates />
                            </RoleRoute>
                        } />
                        <Route path="vlan-profiles" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <VlanProfiles />
                            </RoleRoute>
                        } />
                        <Route path="connected-users" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin', 'support', 'viewer']}>
                                <ConnectedUsers />
                            </RoleRoute>
                        } />
                        {/* Live speeds live in Internet Plans (its Live Bandwidth view) */}
                        <Route path="bandwidth" element={<Navigate to="/plans?tab=bandwidth" replace />} />
                        <Route path="membership-tiers" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin']}>
                                <MembershipTiers />
                            </RoleRoute>
                        } />
                        <Route path="addons" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <Addons />
                            </RoleRoute>
                        } />
                        <Route path="policy-rules" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin']}>
                                <PolicyRules />
                            </RoleRoute>
                        } />
                        {/* Support and Viewer have Reports in their menu (and View on it) */}
                        <Route path="reports" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin', 'support', 'viewer']}>
                                <Reports />
                            </RoleRoute>
                        } />
                        <Route path="traffic" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <Traffic />
                            </RoleRoute>
                        } />
                        <Route path="scheduler-jobs" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <SchedulerJobs />
                            </RoleRoute>
                        } />
                        <Route path="upgrade-approvals" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <UpgradeApprovals />
                            </RoleRoute>
                        } />
                        <Route path="webhooks" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin']}>
                                <Webhooks />
                            </RoleRoute>
                        } />

                        {/* Superadmin + Tenant Admin + Branch Admin */}
                        <Route path="branches" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <Branches />
                            </RoleRoute>
                        } />
                        <Route path="admins" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <Admins />
                            </RoleRoute>
                        } />
                        <Route path="templates" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <Templates />
                            </RoleRoute>
                        } />

                        {/* Visual Landing Page Builder — additive, separate from Templates */}
                        <Route path="landing-pages" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <LandingPages />
                            </RoleRoute>
                        } />
                        <Route path="landing-pages/templates" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <LandingTemplates />
                            </RoleRoute>
                        } />
                        <Route path="landing-pages/:id/edit" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <LandingBuilder />
                            </RoleRoute>
                        } />

                        {/* Superadmin + Tenant Admin for campaigns */}
                        <Route path="campaigns" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin']}>
                                <Campaigns />
                            </RoleRoute>
                        } />

                        {/* Authorization Sets — superadmin + tenant_admin only */}
                        <Route path="auth-sets" element={
                            <RoleRoute roles={['tenant_admin']}>
                                <AuthSets />
                            </RoleRoute>
                        } />

                        {/* All admin roles (superadmin, tenant_admin, branch_admin, support, viewer) */}
                        <Route path="nas" element={<Routers />} />
                        <Route path="plans" element={<Plans />} />
                        <Route path="vouchers" element={<Vouchers />} />
                        <Route path="media" element={<Media />} />
                        <Route path="sessions" element={<Sessions />} />
                        <Route path="session-report" element={<SessionReport />} />
                        <Route path="devices" element={<Devices />} />
                        <Route path="device-status" element={<DeviceStatus />} />
                        <Route path="users" element={<Users />} />
                        <Route path="theme-demo" element={<ThemeDemo />} />
                        <Route path="device-auth" element={<DeviceAuth />} />

                        {/* Hotel room management — Support and Viewer look rooms up (Rooms, User
                            Types & VLANs → View); changing them is Configure */}
                        <Route path="rooms" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin', 'support', 'viewer']}>
                                <Rooms />
                            </RoleRoute>
                        } />

                        {/* Custom per-branch pages (Staff/Banquet/PreCheckin/restaurant/etc) alongside Rooms */}
                        <Route path="branch-pages" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin', 'branch_admin']}>
                                <BranchPages />
                            </RoleRoute>
                        } />

                        {/* PMS Integrations */}
                        <Route path="integrations" element={
                            <RoleRoute roles={['superadmin', 'tenant_admin']}>
                                <Integrations />
                            </RoleRoute>
                        } />

                        {/* DoT Compliance Logs — superadmin only */}
                        <Route path="dot-logs" element={
                            <RoleRoute roles={['superadmin']}>
                                <DOTLogs />
                            </RoleRoute>
                        } />

                        {/* Authorization Session Audit Logs */}
                        <Route path="auth-logs"     element={<AuthLogs />} />
                        <Route path="radius-report" element={<RadiusReport />} />
                        <Route path="auth-logs/:id" element={<AuthLogDetail />} />

                        {/* Centralized Hotspot Logs Hub — federates Session/Syslog/PMS/Audit logs */}
                        <Route path="logs" element={<LogsHub />} />
                        <Route path="guest-activity" element={<GuestActivity />} />
                        <Route path="activity-log" element={<AdminActivity />} />
                        <Route path="logs/sessions/:sessionId" element={<SessionInvestigation />} />

                        {/* PMS Activity Logs (MongoDB) */}
                        <Route path="pms-logs" element={<PmsLogs />} />

                        {/* Access Control — superadmin only */}
                        <Route path="access-control" element={
                            <RoleRoute roles={['superadmin']}>
                                <AccessControl />
                            </RoleRoute>
                        } />

                        {/* Enterprise Syslog Dashboard — superadmin only */}
                        <Route path="syslog" element={
                            <RoleRoute roles={['superadmin']}>
                                <Syslog />
                            </RoleRoute>
                        } />

                        {/* Setup & Configuration Guide — all roles */}
                        <Route path="setup-guide" element={<SetupGuide />} />
                    </Route>

                    {/* 404 Not Found */}
                    <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
            </BrowserRouter>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </QueryClientProvider>
    );
}

export default App;
